import { useState } from "react";
import { useForm } from "react-hook-form";
import QRCode from "react-qr-code";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { CopyableField } from "components/ui/copyable_field/CopyableField";
import Input from "components/ui/input/Input";
import { toast } from "components/ui/toast/Toast";
import { betterAuthErrorText } from "lib/auth/authClient";
import { useVerifyTotp } from "lib/hooks/api/auth/useVerifyTotp";
import Label from "../../ui/input/Label";
import { MfaForm, MfaSetup } from "./utils";

type FormValues = { code: string };

type Props = {
  setup: MfaSetup;
  setSetup: React.Dispatch<React.SetStateAction<MfaSetup | null>>;
  onSuccess: () => Promise<void>;
};

export function VerifyMFA({ setup, setSetup, onSuccess }: Props) {
  const [showSetupLink, setShowSetupLink] = useState(false);
  const { control, handleSubmit, setError } = useForm<FormValues>({
    defaultValues: { code: "" },
  });

  const { mutate: verify, isLoading } = useVerifyTotp({
    onSuccess: async () => {
      setSetup(null);
      toast.success("Two-factor authentication enabled");
      // Refetch the session
      await onSuccess();
    },
    onError: (error) => setError("code", { type: "server", message: betterAuthErrorText(error) }),
  });

  const onVerify = handleSubmit((values) => verify({ code: values.code }));

  return (
    <MfaForm onSubmit={onVerify}>
      {showSetupLink ? (
        <Flex column gap={16}>
          <Text>
            Copy this setup link into your authenticator app or password manager, then enter a code
            to confirm.
          </Text>
          <CopyableField value={setup.totpURI} toastMessage="Setup link copied" wrapValue />
          <Button buttonType="tertiary" onClick={() => setShowSetupLink(false)}>
            Show QR code
          </Button>
        </Flex>
      ) : (
        <Flex column gap={16}>
          <Text>Scan this QR code with your authenticator app, then enter a code to confirm.</Text>
          <QRCodeContainer>
            <QRCode value={setup.totpURI} size={160} />
          </QRCodeContainer>
          <Button buttonType="tertiary" onClick={() => setShowSetupLink(true)}>
            Can't scan the code?
          </Button>
        </Flex>
      )}
      <BackupCodesList
        codes={setup.backupCodes}
        label="Backup codes (store these somewhere safe):"
      />
      <Flex column gap="4px">
        <Label htmlFor="code">Authentication code</Label>
        <Input
          name="code"
          control={control}
          placeholder="123456"
          rules={{ required: { value: true, message: "A code is required" } }}
        />
      </Flex>
      <Button buttonType="primary" type="submit" loading={isLoading}>
        Verify and turn on
      </Button>
    </MfaForm>
  );
}

const QRCodeContainer = styled.div(({ theme }) => ({
  background: theme.colors.sysBackgroundDefault,
  padding: 16,
  paddingBottom: 8,
  width: "fit-content",
  alignSelf: "center",
}));

export function BackupCodesList({ codes, label }: { codes: string[]; label: string }) {
  if (codes.length === 0) return null;
  return (
    <Flex gap={8} flexDirection="column">
      <Text>
        <strong>{label}</strong>
      </Text>
      <ul aria-label="Backup codes">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ul>
    </Flex>
  );
}
